import {FC} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import ActionButton from 'jbc-front/components/ActionButton'
import Tag from 'jbc-front/components/Tag'
import {MultiSelect, Option} from 'jbc-front/components/presenters/form/Select'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {useDebouncedSubmit} from 'hooks/useDebouncedSubmit'
import {schema, Schema} from '../../schema'
import {convertToOption, AlertTypeOption} from '../../query'
import {DateRow} from './DateRow'
import styles from './SearchForm.scss'

interface SearchFormProps {
  values: Schema
  onSubmit: (data: Schema) => void
  onClear: () => void
  hasConditions: (data: Schema) => boolean
  alertTypeList: AlertTypeOption[]
}

type FilterStatusKey = 'all' | 'in_progress' | 'completed'
type FilterStatuses = {key: FilterStatusKey; label: string}[]
const statuses: FilterStatuses = [
  {key: 'all', label: 'すべて'},
  {key: 'in_progress', label: '進行中'},
  {key: 'completed', label: '完了'},
]

const IGNORE_AUTO_SUBMIT_FIELDS = ['alertStatus', 'alertTypeIds', 'limit', 'sortType']

export const SearchForm: FC<SearchFormProps> = ({values, onSubmit, onClear, hasConditions, alertTypeList}) => {
  const convertedOptions = convertToOption(alertTypeList)
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    register,
    subscribe,
    watch,
    formState: {errors},
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    values,
  })

  const formValues = watch()
  const {alertStatus} = formValues

  useDebouncedSubmit(subscribe, () => handleSubmit(onSubmit)(), {
    ignoreAutoSubmitFields: IGNORE_AUTO_SUBMIT_FIELDS,
  })

  return (
    <div className={styles.searchForm}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.mainSearchFields}>
          <div className={styles.mainFormControls}>
            <div className={styles.mainFormControl}>
              <Input {...register('searchValue')} style={{width: 230, height: 30}} placeholder="氏名、スタッフコード" />
            </div>
            <ActionButton primary={hasConditions(formValues)} onClick={() => onClear()}>
              条件解除
            </ActionButton>
          </div>
        </div>

        <div className={styles.details}>
          <div className={styles.detailsRow}>
            <div className={styles.detailsHeader}>ステータス</div>
            <div className={styles.detailsBody}>
              <div className={styles.detailsBodyItems}>
                {statuses.map((status) => (
                  <Tag
                    key={status.key}
                    active={alertStatus === status.key}
                    onClick={() => {
                      setValue('alertStatus', status.key)
                      handleSubmit(onSubmit)()
                    }}
                  >
                    {status.label}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
          <Spacer direction="y" size={1} />

          <div className={styles.detailsRow}>
            <div className={styles.detailsHeader}>アラート名</div>
            <div className={styles.detailsBody}>
              <div className={styles.alertTypeSelect}>
                <Controller
                  name="alertTypeIds"
                  control={control}
                  render={({field}) => (
                    <MultiSelect
                      options={convertedOptions}
                      value={convertedOptions.filter((opt) => field.value.includes(opt.value))}
                      onChange={(val: Option[]) => {
                        field.onChange(val.map((v) => v.value))
                        handleSubmit(onSubmit)()
                      }}
                      noOptionsMessage={() => 'データが見つかりません'}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <Spacer direction="y" size={1} />

          <DateRow
            header="通知日"
            names={['alertStartStartOn', 'alertStartEndOn']}
            control={control}
            errorMessage={errors?.alertStartStartOn?.message || errors?.alertStartEndOn?.message}
            setValue={setValue}
            getValues={getValues}
          />
          <Spacer direction="y" size={1} />

          <DateRow
            header="基準日"
            names={['baseDateStartOn', 'baseDateEndOn']}
            control={control}
            errorMessage={errors?.baseDateStartOn?.message || errors?.baseDateEndOn?.message}
            setValue={setValue}
            getValues={getValues}
          />
        </div>
      </form>
    </div>
  )
}
